import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Alert,
  Autocomplete
} from "@mui/material";
import ShiftExchangeService from "../service/ShiftExchangeService";
import HeaderBannerPage from "../components/HeaderBannerPage";

export default function ShiftExchange() {
  // Search States
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchDate, setSearchDate] = useState("");
  
  // Data States
  const [allUsers, setAllUsers] = useState([]);
  const [rosterData, setRosterData] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Exchange Box States
  const [box1, setBox1] = useState(null);
  const [box2, setBox2] = useState(null);

  // Updated List State
  const [updatedList, setUpdatedList] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, rosterRes, scheduleRes] = await Promise.all([
        ShiftExchangeService.getAllUsers(),
        ShiftExchangeService.getRosterDetails(),
        ShiftExchangeService.getScheduleDetails()
      ]);

      // Handle User API response structure
      // User provided example returns: { resultSet: [...] }
      const users = usersRes.data.resultSet || usersRes.data || [];
      setAllUsers(users);

      // Handle Roster/Schedule response structure (assuming array or inside result property)
      // Ensure we extract the array correctly even if wrapped in resultSet or result
      let rosterRaw = rosterRes.data;
      let rosterArray = [];
      if (Array.isArray(rosterRaw)) {
          rosterArray = rosterRaw;
      } else if (rosterRaw && Array.isArray(rosterRaw.resultSet)) {
          rosterArray = rosterRaw.resultSet;
      } else if (rosterRaw && Array.isArray(rosterRaw.result)) {
          rosterArray = rosterRaw.result;
      }
      setRosterData(rosterArray);

      let scheduleRaw = scheduleRes.data;
      let scheduleArray = [];
      if (Array.isArray(scheduleRaw)) {
          scheduleArray = scheduleRaw;
      } else if (scheduleRaw && Array.isArray(scheduleRaw.resultSet)) {
          scheduleArray = scheduleRaw.resultSet;
      } else if (scheduleRaw && Array.isArray(scheduleRaw.result)) {
          scheduleArray = scheduleRaw.result;
      }
      setScheduleData(scheduleArray);
      
    } catch (err) {
      console.error("Error fetching data", err);
      setError("Failed to load initial data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getShiftDetailsInfo = (shiftCode) => {
    return scheduleData.find(s => s.shift_code === shiftCode) || { shift_name: shiftCode, StartTime: "", EndTime: "" };
  };

  const handleSearch = () => {
    if (!searchDate) {
      setError("Please select a date to search for shifts.");
      return;
    }
    setError("");

    // Determine users to search: selected user only, or all users if none selected (optional, but requirements imply single user search mostly)
    // Requirement said: "check paticular employee shift details by searching employee ID , employee name"
    // And now: "employee id and employee name must be have in dropdown to select"
    
    let matchingUsers = allUsers;
    if (selectedUser) {
        matchingUsers = [selectedUser];
    } else {
        // If no user selected, maybe show alert or just show nothing? 
        // Or if we want to allow searching generally without ID, we can keep allUsers.
        // But for now let's stick to requiring at least a user or date.
    }

    // Helper to compare dates
    // Search Date is YYYY-MM-DD
    // Roster Date is M/D/YYYY h:mm:ss A (e.g., "2/1/2026 12:00:00 AM")
    const isSameDate = (rosterDateStr, searchDateStr) => {
        if (!rosterDateStr) return false;
        
        // Try simple string match first if formats align
        if (rosterDateStr.startsWith(searchDateStr)) return true;

        // Parse roster date
        const rDate = new Date(rosterDateStr);
        const sDate = new Date(searchDateStr);

        // Check validity
        if (isNaN(rDate.getTime()) || isNaN(sDate.getTime())) return false;

        return rDate.getFullYear() === sDate.getFullYear() &&
               rDate.getMonth() === sDate.getMonth() &&
               rDate.getDate() === sDate.getDate();
    };

    const results = matchingUsers.map(user => {
        // Find roster entry
        const rosterEntry = rosterData.find(r => 
            String(r.service_no) === String(user.ServiceNo) && 
            isSameDate(r.WorkDate, searchDate)
        );

        if (!rosterEntry) return null;

        const shiftInfo = getShiftDetailsInfo(rosterEntry.shift_code);

        return {
            ...user, // User details
            shift_code: rosterEntry.shift_code,
            shift_name: shiftInfo.shift_name,
            StartTime: shiftInfo.StartTime,
            EndTime: shiftInfo.EndTime,
            WorkDate: rosterEntry.WorkDate
        };
    }).filter(item => item !== null);

    setFilteredResults(results);
    if (results.length === 0) {
        setError("No shift records found for the given criteria.");
    }
  };

  const addToBox1 = (employee) => {
    setBox1(employee);
    setError(""); // Clear errors
  };

  const addToBox2 = (employee) => {
    setBox2(employee);
    setError("");
  };

  const handleExchange = () => {
    if (!box1 || !box2) {
      setError("Please select two employees to exchange.");
      return;
    }

    if (box1.LocationName !== box2.LocationName) {
      setError(`Cannot exchange shifts. Locations do not match: ${box1.LocationName} vs ${box2.LocationName}`);
      return;
    }

    // Perform Visual Swap
    const newBox1 = { 
        ...box1, 
        shift_code: box2.shift_code, 
        shift_name: box2.shift_name, 
        StartTime: box2.StartTime, 
        EndTime: box2.EndTime,
        WorkDate: box2.WorkDate
    };
    const newBox2 = { 
        ...box2, 
        shift_code: box1.shift_code, 
        shift_name: box1.shift_name, 
        StartTime: box1.StartTime, 
        EndTime: box1.EndTime,
        WorkDate: box1.WorkDate
    };

    setBox1(newBox1);
    setBox2(newBox2);
    setSuccessMsg("Shifts exchanged visually. Click Save to commit changes.");
  };

  const handleSave = async () => {
    if (!box1 || !box2) return;

    // Prepare Payload
    // The visual swap already happened in state box1/box2
    // So we just send the current state of box1 and box2 to the backend
    
    // Payload list
    const payload = [
        {
            service_no: box1.ServiceNo,
            WorkDate: box1.WorkDate, 
            shift_code: box1.shift_code
        },
        {
            service_no: box2.ServiceNo,
            WorkDate: box2.WorkDate,
            shift_code: box2.shift_code
        }
    ];

    try {
        await ShiftExchangeService.postEmployeeRoster(payload);
        setSuccessMsg("Successfully updated shift details.");
        
        // Add to updated list
        setUpdatedList(prev => [...prev, box1, box2]);

        // Refresh Data? Or update local state?
        // Updating local roster state to reflect changes without re-fetch
        // But for consistency we might want to re-fetch roster or manually update rosterData
        // For now, let's just leave it.
        setBox1(null);
        setBox2(null);
        setFilteredResults([]); // Clear search to avoid confusion
    } catch (err) {
        console.error("Save failed", err);
        setError("Failed to save changes.");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#FFFFFF",
      }}
    >
      <HeaderBannerPage />
      <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
      <Typography variant="h4" gutterBottom>Employee Link / Shift Exchange</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Search Employee Shifts</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            {/* User Selection Dropdown (Search by ID or Name) */}
            <Autocomplete
                options={allUsers}
                getOptionLabel={(option) => `${option.ServiceNo} - ${option.Name}`}
                value={selectedUser}
                onChange={(event, newValue) => {
                    setSelectedUser(newValue);
                }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        label="Select Employee (ID or Name)" 
                        fullWidth 
                    />
                )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField 
                type="date" 
                fullWidth 
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                label="Select Date"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="contained" onClick={handleSearch} fullWidth size="large">Search</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Search Results */}
      {filteredResults.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 300 }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Service No</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Shift</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredResults.map((emp) => (
                        <TableRow key={emp.ServiceNo}>
                            <TableCell>{emp.ServiceNo}</TableCell>
                            <TableCell>{emp.Name}</TableCell>
                            <TableCell>{emp.LocationName}</TableCell>
                            <TableCell>{emp.WorkDate}</TableCell>
                            <TableCell>{emp.shift_name}</TableCell>
                            <TableCell>{emp.StartTime} - {emp.EndTime}</TableCell>
                            <TableCell>
                                <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => addToBox1(emp)}>To Box 1</Button>
                                <Button size="small" variant="outlined" onClick={() => addToBox2(emp)}>To Box 2</Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
      )}

      {/* Exchange Area */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" color="primary">Employee 1 (Box 1)</Typography>
                    {box1 ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography><strong>Name:</strong> {box1.Name} ({box1.ServiceNo})</Typography>
                            <Typography><strong>Location:</strong> {box1.LocationName}</Typography>
                            <Typography><strong>Date:</strong> {box1.WorkDate}</Typography>
                            <Typography><strong>Shift:</strong> {box1.shift_name} ({box1.shift_code})</Typography>
                            <Typography><strong>Time:</strong> {box1.StartTime} - {box1.EndTime}</Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ mt: 2, fontStyle: 'italic', color: 'gray' }}>No employee selected</Typography>
                    )}
                </CardContent>
            </Card>
        </Grid>
        
        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <Button 
                variant="contained" 
                color="secondary" 
                onClick={handleExchange}
                disabled={!box1 || !box2}
                sx={{ mb: 2 }}
            >
                Exchange Shifts
            </Button>
            <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={!box1 || !box2}
            >
                Save Changes
            </Button>
        </Grid>

        <Grid item xs={12} md={5}>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" color="primary">Employee 2 (Box 2)</Typography>
                    {box2 ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography><strong>Name:</strong> {box2.Name} ({box2.ServiceNo})</Typography>
                            <Typography><strong>Location:</strong> {box2.LocationName}</Typography>
                            <Typography><strong>Date:</strong> {box2.WorkDate}</Typography>
                            <Typography><strong>Shift:</strong> {box2.shift_name} ({box2.shift_code})</Typography>
                            <Typography><strong>Time:</strong> {box2.StartTime} - {box2.EndTime}</Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ mt: 2, fontStyle: 'italic', color: 'gray' }}>No employee selected</Typography>
                    )}
                </CardContent>
            </Card>
        </Grid>
      </Grid>

      {/* Updated Logs */}
      {updatedList.length > 0 && (
          <Box sx={{ mt: 4 }}>
              <Typography variant="h6">Recently Updated Shifts</Typography>
              <TableContainer component={Paper}>
                  <Table>
                      <TableHead>
                          <TableRow>
                              <TableCell>Service No</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>New Shift</TableCell>
                              <TableCell>New Time</TableCell>
                              <TableCell>Date</TableCell>
                          </TableRow>
                      </TableHead>
                      <TableBody>
                          {updatedList.map((item, index) => (
                              <TableRow key={index}>
                                  <TableCell>{item.ServiceNo}</TableCell>
                                  <TableCell>{item.Name}</TableCell>
                                  <TableCell>{item.shift_name} ({item.shift_code})</TableCell>
                                  <TableCell>{item.StartTime} - {item.EndTime}</TableCell>
                                  <TableCell>{item.WorkDate}</TableCell>
                              </TableRow>
                          ))}
                      </TableBody>
                  </Table>
              </TableContainer>
          </Box>
      )}

      </Box>
    </Box>
  );
}
